import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import './BillingScreen.css';

function CreditCardIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}

function CheckCircleIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function formatCents(cents) {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

function formatDate(zonedDateTime) {
  if (!zonedDateTime) return '—';
  const d = typeof zonedDateTime === 'string' ? new Date(zonedDateTime) : zonedDateTime;
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function BillingScreen() {
  const [searchParams] = useSearchParams();
  const setupSuccess = searchParams.get('setup') === 'success';

  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [planChangeSuccess, setPlanChangeSuccess] = useState(false);

  const loadBilling = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const data = await api.getBillingStatus();
      setBilling(data);
    } catch (e) {
      setError(e?.message || 'Failed to load billing.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();
  }, []);

  const handleSubscribe = async (tierId) => {
    setError(null);
    setPlanChangeSuccess(false);
    setCheckoutTier(tierId);
    try {
      const res = await api.createBillingCheckout(tierId);
      if (res?.updated === true) {
        await loadBilling(false);
        setPlanChangeSuccess(true);
        setTimeout(() => setPlanChangeSuccess(false), 4000);
      } else if (res?.url) {
        window.location.href = res.url;
        return;
      } else {
        setError('Could not start checkout.');
      }
    } catch (e) {
      setError(e?.message || 'Could not start checkout.');
    } finally {
      setCheckoutTier(null);
    }
  };

  const handleManageBilling = async () => {
    setError(null);
    setPortalLoading(true);
    try {
      const res = await api.createBillingPortalSession();
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      setError('Could not open billing portal.');
    } catch (e) {
      setError(e?.message || 'Could not open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="vt-pageHead">
        <h1 className="vt-pageTitle">Billing</h1>
        <p className="vt-pageSub vt-muted">Loading…</p>
      </div>
    );
  }

  const plans = billing?.plans || [];
  const hasSubscription = billing?.hasActiveSubscription === true;
  const tier = billing?.tier;
  const periodEnd = billing?.periodEnd;
  const verificationCount = billing?.verificationCount ?? 0;
  const included = billing?.includedVerifications ?? 0;
  const overageCount = billing?.overageCount ?? 0;
  const overageCents = billing?.overageCentsPerUnit ?? 10;

  return (
    <>
      <div className="vt-pageHead">
        <h1 className="vt-pageTitle">Billing</h1>
        <p className="vt-pageSub vt-muted">
          Manage your subscription and usage. Each plan includes a monthly verification allowance with flexible overage.
        </p>
      </div>

      {setupSuccess && (
        <div className="vt-billingBanner vt-billingBanner--success" role="alert">
          <CheckCircleIcon className="vt-billingBannerIcon" />
          <span>Subscription set up successfully.</span>
        </div>
      )}

      {planChangeSuccess && (
        <div className="vt-billingBanner vt-billingBanner--success" role="alert">
          <CheckCircleIcon className="vt-billingBannerIcon" />
          <span>Plan updated successfully. Proration applied to your next invoice.</span>
        </div>
      )}

      {error && (
        <div className="vt-billingBanner vt-billingBanner--error" role="alert">
          {error}
        </div>
      )}

      {tier === 'free' && hasSubscription && verificationCount >= included && included > 0 && (
        <div className="vt-billingBanner vt-billingBanner--warning" role="alert">
          You've reached your free tier limit of {included.toLocaleString()} verifications this month. Upgrade your plan to continue.
        </div>
      )}

      {hasSubscription && (
        <div className="vt-billingCard vt-surface vt-card vt-billingStatusCard">
          <div className="vt-billingCardHeader">
            <CreditCardIcon className="vt-billingCardIcon" />
            <div className="vt-billingCardHeaderText">
              <h2 className="vt-billingCardTitle">Current plan</h2>
              <p className="vt-billingCardSub vt-muted">
                {tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : '—'} · Renews {formatDate(periodEnd)}
              </p>
            </div>
          </div>
          <div className="vt-billingCardBody">
            <div className="vt-billingRow">
              <span className="vt-billingLabel">Usage this period</span>
              <span className="vt-billingValue">
                {verificationCount.toLocaleString()} / {included.toLocaleString()} verifications
              </span>
            </div>
            {tier === 'free' && verificationCount >= included && (
              <p className="vt-billingUpgradeHint vt-muted">
                At limit. Upgrade to add more verifications.
              </p>
            )}
            {overageCount > 0 && (
              <div className="vt-billingRow">
                <span className="vt-billingLabel">Overage</span>
                <span className="vt-billingValue">
                  {overageCount.toLocaleString()} × {formatCents(overageCents)} ≈ {formatCents(overageCount * overageCents)}
                </span>
              </div>
            )}
          </div>
          <div className="vt-billingCardActions">
            <button
              type="button"
              className="vt-btn vt-btn--secondary"
              onClick={handleManageBilling}
              disabled={portalLoading}
            >
              {portalLoading ? 'Opening…' : 'Manage billing'}
            </button>
          </div>
        </div>
      )}

      <div className="vt-billingPlans">
        <h2 className="vt-billingPlansTitle">{hasSubscription ? 'Available plans' : 'Choose your plan'}</h2>
        <p className="vt-billingPlansSub vt-muted">
          Scale with your verification volume. All plans include API access and usage-based overage.
        </p>
        <div className="vt-billingPlansGrid">
          {plans.map((plan) => {
            const isCurrent = hasSubscription && tier === plan.id;
            const isContactSales = plan.contactSales === true;
            return (
              <div
                key={plan.id}
                className={`vt-billingPlanCard vt-surface vt-card ${isCurrent ? 'vt-billingPlanCard--current' : ''} ${isContactSales ? 'vt-billingPlanCard--contactSales' : ''}`}
              >
                <div className="vt-billingPlanCardInner">
                  <h3 className="vt-billingPlanName">{plan.name}</h3>
                  {isContactSales ? (
                    <div className="vt-billingPlanPriceWrap">
                      <span className="vt-billingPlanContactSales">Contact sales</span>
                    </div>
                  ) : (
                    <div className="vt-billingPlanPriceWrap">
                      <span className="vt-billingPlanPrice">{formatCents(plan.priceCents)}</span>
                      <span className="vt-billingPlanPeriod">/month</span>
                    </div>
                  )}
                  {!isContactSales && (
                    <>
                      <p className="vt-billingPlanIncluded">
                        <span className="vt-billingPlanIncludedNum">{plan.includedVerifications.toLocaleString()}</span>
                        {' '}verifications included
                      </p>
                      {plan.freeTier ? (
                        <p className="vt-billingPlanOverage">
                          Upgrade to add more
                        </p>
                      ) : (
                        <p className="vt-billingPlanOverage">
                          Overage: {formatCents(plan.overageCentsPerUnit)} per verification
                        </p>
                      )}
                    </>
                  )}
                  {isCurrent ? (
                    <div className="vt-billingPlanCurrent">
                      <CheckCircleIcon className="vt-billingPlanCurrentIcon" />
                      <span>Current plan</span>
                    </div>
                  ) : isContactSales ? (
                    <a
                      href="mailto:sales@veritage.io?subject=Enterprise%20plan%20inquiry"
                      className="vt-btn vt-btn--primary vt-billingPlanCta"
                    >
                      Contact sales
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="vt-btn vt-btn--primary vt-billingPlanCta"
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={checkoutTier != null}
                    >
                      {checkoutTier === plan.id ? 'Redirecting…' : (hasSubscription ? 'Switch plan' : 'Get started')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
